/**
 * Anuncios recompensados.
 *
 * **Lo que hay que entender de este fichero:** aquí no se decide ninguna
 * recompensa. El recorrido es siempre
 *
 *     pedir el vale  ->  ver el anuncio  ->  PREGUNTAR al servidor qué me toca
 *
 * y el último paso es el único que entrega algo. El navegador no puede
 * acreditarse monedas ni revivires por su cuenta, porque el servidor solo
 * acredita cuando Adsgram se lo confirma por su lado.
 *
 * Por eso, después de que el vídeo acabe, esto **pregunta varias veces**: el
 * aviso de Adsgram viaja de sus servidores a los nuestros y puede llegar un
 * segundo después de que el jugador cierre el vídeo. Preguntar es seguro, y
 * repetir la pregunta no cobra dos veces.
 *
 * MODO DEV. Fuera de Telegram no hay servidor ni Adsgram, así que se usa un
 * **anuncio de prueba**: un panel con cuenta atrás que se comporta como el de
 * verdad. Existe para poder abrir el juego y usar la función de principio a
 * fin sin cuenta de anunciante, que es lo que Adsgram no permite hasta tener
 * 50.000 usuarios al día.
 */

import { modoDev, pedir } from './api';
import { t } from '../i18n';

/** Los cuatro momentos del spec. Los nombres los manda el servidor. */
export type Momento = 'x2_inicio' | 'revivir' | 'duelo_x2' | 'caja_diaria';

export interface ConfigAnuncios {
  activos: boolean;
  blockId: string;
  x2Disponible: boolean;
  cajaDiariaLista: boolean;
  revivirRestantes: number;
  cajaDiariaMonedas: number;
  esperaMinimaSeg: number;
  soyAdmin: boolean;
}

export interface Concesion {
  concedido: boolean;
  momento: string;
  monedas: number;
  revivirConcedido: boolean;
  revivirRestantes: number;
  x2Listo: boolean;
  pendiente: boolean;
  motivo: string | null;
  monedasTotales: number;
}

/** Lo que devuelve el SDK de Adsgram al acabar. Copiado de su documentación. */
interface ResultadoAdsgram {
  done: boolean;
  description: string;
  state: 'load' | 'render' | 'playing' | 'destroy';
  error: boolean;
}

interface ControladorAdsgram {
  show(): Promise<ResultadoAdsgram>;
  destroy(): void;
}

declare global {
  interface Window {
    Adsgram?: {
      init(opciones: { blockId: string; debug?: boolean }): ControladorAdsgram;
    };
  }
}

// La URL del SDK, de la documentación oficial de Adsgram.
const SDK = 'https://sad.adsgram.ai/js/sad.min.js';

const CONFIG_VACIA: ConfigAnuncios = {
  activos: false,
  blockId: '',
  x2Disponible: false,
  cajaDiariaLista: false,
  revivirRestantes: 0,
  cajaDiariaMonedas: 0,
  esperaMinimaSeg: 15,
  soyAdmin: false,
};

// ---------------------------------------------------------------------------
// El SDK
// ---------------------------------------------------------------------------

let promesaSdk: Promise<boolean> | null = null;

/**
 * Carga el SDK de Adsgram, una sola vez.
 *
 * Se carga aquí y no en el `index.html` a propósito: con los anuncios apagados
 * nadie se descarga nada. El presupuesto del juego son 1,5 MB en un móvil con
 * datos móviles, y un script de un tercero que no se va a usar es peso regalado.
 */
function cargarSdk(): Promise<boolean> {
  if (window.Adsgram) return Promise.resolve(true);
  if (promesaSdk) return promesaSdk;

  promesaSdk = new Promise<boolean>((resolver) => {
    const etiqueta = document.createElement('script');
    etiqueta.src = SDK;
    etiqueta.async = true;
    etiqueta.onload = () => resolver(Boolean(window.Adsgram));
    // Si el script no carga (sin red, bloqueador, dominio caído), no se
    // rompe nada: se devuelve false y el jugador ve "no hay anuncios ahora",
    // que es la verdad.
    etiqueta.onerror = () => resolver(false);
    document.head.appendChild(etiqueta);
  });
  return promesaSdk;
}

const controladores = new Map<string, ControladorAdsgram>();

function controlador(blockId: string): ControladorAdsgram | null {
  if (!window.Adsgram) return null;
  let c = controladores.get(blockId);
  if (!c) {
    c = window.Adsgram.init({ blockId });
    controladores.set(blockId, c);
  }
  return c;
}

// ---------------------------------------------------------------------------
// El anuncio de prueba (modo DEV y respaldo)
// ---------------------------------------------------------------------------

/**
 * Un anuncio simulado, con cuenta atrás de verdad.
 *
 * No es un atajo para saltarse el anuncio: el botón de cerrar **no aparece**
 * hasta que acaba la cuenta, igual que en un anuncio recompensado real. Así se
 * puede comprobar a mano lo que siente el jugador —cuánto molesta, si la
 * recompensa compensa la espera— que es justo lo que no se puede saber leyendo
 * código.
 */
function anuncioDePrueba(segundos: number): Promise<boolean> {
  return new Promise((resolver) => {
    const capa = document.createElement('div');
    capa.className = 'anuncioPrueba';
    capa.innerHTML =
      `<div class="apCaja">` +
      `<div class="apSello">${t().anuncioDePrueba}</div>` +
      `<div class="apCuenta">${segundos}</div>` +
      `<div class="apPie">${t().anuncioPruebaPie}</div>` +
      `<button class="boton apCerrar" hidden>${t().continuar}</button>` +
      `</div>`;
    document.body.appendChild(capa);

    const cuenta = capa.querySelector<HTMLElement>('.apCuenta')!;
    const cerrar = capa.querySelector<HTMLButtonElement>('.apCerrar')!;
    let quedan = segundos;

    const reloj = window.setInterval(() => {
      quedan -= 1;
      cuenta.textContent = String(Math.max(0, quedan));
      if (quedan <= 0) {
        window.clearInterval(reloj);
        cerrar.hidden = false;
        cerrar.focus();
      }
    }, 1000);

    cerrar.addEventListener('click', () => {
      capa.remove();
      resolver(true);
    });
  });
}

// ---------------------------------------------------------------------------
// Estado local del modo DEV
// ---------------------------------------------------------------------------

const CLAVE_DEV = 'moonrush.dev.anuncios';

interface EstadoDev {
  dia: string;
  cajasHoy: number;
  x2Listo: boolean;
}

function hoy(): string {
  return new Date().toISOString().slice(0, 10);
}

function devEstado(): EstadoDev {
  const vacio: EstadoDev = { dia: hoy(), cajasHoy: 0, x2Listo: false };
  try {
    const crudo = localStorage.getItem(CLAVE_DEV);
    if (!crudo) return vacio;
    const e = JSON.parse(crudo) as EstadoDev;
    // La caja es una al día: si cambió el día, vuelve a estar disponible.
    return e.dia === hoy() ? e : { ...vacio, x2Listo: e.x2Listo };
  } catch {
    return vacio;
  }
}

function devGuardar(e: EstadoDev): void {
  try {
    localStorage.setItem(CLAVE_DEV, JSON.stringify(e));
  } catch {
    /* ventana privada: se pierde al cerrar, no es grave */
  }
}

/** Revivires gastados en la partida en curso, solo para el modo DEV. */
let devRevivires = 0;
export function devReiniciarRevivires(): void {
  devRevivires = 0;
}

const DEV_REVIVIRES_MAX = 3;
const DEV_CAJA_MONEDAS = 1500;

// ---------------------------------------------------------------------------
// La API
// ---------------------------------------------------------------------------

export const anuncios = {
  /** Qué anuncios se pueden ver ahora mismo. Lo decide el servidor. */
  async config(runId?: string): Promise<ConfigAnuncios> {
    if (modoDev) {
      const e = devEstado();
      return {
        ...CONFIG_VACIA,
        // En modo DEV los anuncios están encendidos para poder probarlos. En
        // producción los enciende el dueño desde el bot o el panel, y arrancan
        // apagados.
        activos: true,
        blockId: 'dev',
        x2Disponible: !e.x2Listo,
        cajaDiariaLista: e.cajasHoy < 1,
        revivirRestantes: Math.max(0, DEV_REVIVIRES_MAX - devRevivires),
        cajaDiariaMonedas: DEV_CAJA_MONEDAS,
        esperaMinimaSeg: 5,
        soyAdmin: true,
      };
    }
    try {
      const ruta = runId
        ? `/ads/config?runId=${encodeURIComponent(runId)}`
        : '/ads/config';
      return await pedir<ConfigAnuncios>(ruta);
    } catch {
      // Si no se puede preguntar, no se ofrece nada. Un botón que falla al
      // tocarlo es peor que un botón que no está.
      return CONFIG_VACIA;
    }
  },

  /**
   * El recorrido completo: pedir el vale, ver el anuncio y preguntar qué toca.
   *
   * Devuelve siempre una `Concesion`, nunca lanza: quien llama a esto está en
   * medio de una partida y una excepción le dejaría la pantalla a medias.
   */
  async ver(
    momento: Momento,
    opciones: { runId?: string; duelId?: string } = {}
  ): Promise<Concesion> {
    if (modoDev) return verEnDev(momento);

    const nada = (motivo: string): Concesion => ({
      concedido: false,
      momento,
      monedas: 0,
      revivirConcedido: false,
      revivirRestantes: 0,
      x2Listo: false,
      pendiente: false,
      motivo,
      monedasTotales: 0,
    });

    // 1. El vale. Si el servidor dice que no, se para aquí: enseñarle un
    //    anuncio y después decirle que no puede es la peor opción posible.
    let vale: { nonce: string; blockId: string; esperaMinimaSeg: number };
    try {
      vale = await pedir('/ads/offer', {
        momento,
        runId: opciones.runId ?? null,
        duelId: opciones.duelId ?? null,
      });
    } catch (err) {
      return nada(mensajeDeError(err));
    }

    // 2. El anuncio. Se avisa al servidor de que arranca ANTES de mostrarlo:
    //    esa marca de tiempo es la que permite saber después si pasó el tiempo
    //    de un vídeo.
    try {
      await pedir('/ads/accept', { nonce: vale.nonce });
    } catch {
      // No es fatal: el aviso de Adsgram sigue pudiendo completar el vale.
      console.warn('no pude avisar de que el anuncio arranca');
    }

    const visto = await mostrarAnuncio(vale.blockId, vale.esperaMinimaSeg);
    if (!visto) return nada(t().anuncioNoCompletado);

    // 3. Preguntar qué toca. Varias veces, porque el aviso de Adsgram puede
    //    llegar a nuestro servidor después de que el vídeo se cierre aquí.
    for (let intento = 0; intento < 10; intento += 1) {
      try {
        const c = await pedir<Concesion>('/ads/claim', { nonce: vale.nonce });
        if (c.concedido) return c;
        if (!c.pendiente) return c;
      } catch (err) {
        return nada(mensajeDeError(err));
      }
      await esperar(1200);
    }
    return nada(t().anuncioSinConfirmar);
  },
};

async function mostrarAnuncio(blockId: string, segundos: number): Promise<boolean> {
  const hay = await cargarSdk();
  const c = hay ? controlador(blockId) : null;
  if (!c) {
    // Sin SDK no hay anuncio de verdad. Se enseña el de prueba para que el
    // recorrido se pueda seguir probando, pero **la recompensa la sigue
    // decidiendo el servidor**: si Adsgram no avisó, no se acredita nada. Un
    // anuncio de prueba no es una recompensa.
    return anuncioDePrueba(segundos);
  }
  try {
    const r = await c.show();
    // `done` es lo que dice la documentación de Adsgram para "visto entero".
    return r.done === true && r.error !== true;
  } catch {
    // El SDK rechaza la promesa cuando el jugador se salta el anuncio. No es
    // un error: es que no lo vio.
    return false;
  }
}

async function verEnDev(momento: Momento): Promise<Concesion> {
  const base: Concesion = {
    concedido: false,
    momento,
    monedas: 0,
    revivirConcedido: false,
    revivirRestantes: 0,
    x2Listo: false,
    pendiente: false,
    motivo: null,
    monedasTotales: 0,
  };

  if (momento === 'revivir' && devRevivires >= DEV_REVIVIRES_MAX) {
    return { ...base, motivo: t().sinMasRevivires };
  }

  const visto = await anuncioDePrueba(5);
  if (!visto) return { ...base, motivo: t().anuncioNoCompletado };

  const e = devEstado();
  if (momento === 'revivir') {
    devRevivires += 1;
    return {
      ...base,
      concedido: true,
      revivirConcedido: true,
      revivirRestantes: Math.max(0, DEV_REVIVIRES_MAX - devRevivires),
    };
  }
  if (momento === 'x2_inicio') {
    devGuardar({ ...e, x2Listo: true });
    return { ...base, concedido: true, x2Listo: true };
  }
  if (momento === 'caja_diaria') {
    devGuardar({ ...e, dia: hoy(), cajasHoy: e.cajasHoy + 1 });
    return { ...base, concedido: true, monedas: DEV_CAJA_MONEDAS };
  }
  return { ...base, motivo: t().errorGenerico };
}

/** Gasta el x2 del modo DEV. En producción lo gasta el servidor al empezar. */
export function devGastarX2(): boolean {
  if (!modoDev) return false;
  const e = devEstado();
  if (!e.x2Listo) return false;
  devGuardar({ ...e, x2Listo: false });
  return true;
}

function esperar(ms: number): Promise<void> {
  return new Promise((r) => window.setTimeout(r, ms));
}

/**
 * El texto que se le enseña al jugador cuando el servidor dice que no.
 *
 * El servidor manda mensajes escritos para una persona (`detail`), así que si
 * hay uno se usa: "Ya has abierto la caja de hoy" explica mucho mejor que
 * "error 409".
 */
function mensajeDeError(err: unknown): string {
  const texto = String(err);
  const marca = texto.indexOf('{"detail":"');
  if (marca >= 0) {
    const resto = texto.slice(marca + 11);
    const fin = resto.indexOf('"');
    if (fin > 0) return resto.slice(0, fin);
  }
  if (texto.includes('409')) return t().anuncioNoDisponible;
  return t().sinConexion;
}
